import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-background/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <span className="font-semibold">Privacy</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-invert prose-sm max-w-none">
          <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last Updated: January 7, 2024</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              This Privacy Policy explains how YASNO AI ("we," "us," or "our") collects, uses, and protects your information when you use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Account information (name, email, business details)</li>
              <li>Social media credentials and access tokens</li>
              <li>Voice commands and text instructions</li>
              <li>Payment information</li>
              <li>Communications with our support team</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">2.2 Information We Collect Automatically</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Usage data and interaction patterns</li>
              <li>Device information and IP addresses</li>
              <li>Cookies and tracking technologies</li>
              <li>Performance metrics of your campaigns</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">2.3 Information from Social Media Platforms</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">When you connect social media accounts, we collect:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Profile information</li>
              <li>Page/account performance data</li>
              <li>Audience demographics</li>
              <li>Post engagement metrics</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">3. How We Use Your Information</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">3.1 Service Provision</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Creating and publishing marketing content</li>
              <li>Managing your social media accounts</li>
              <li>Generating AI-created content</li>
              <li>Providing analytics and reports</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">3.2 Service Improvement</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Training and improving our AI models</li>
              <li>Developing new features</li>
              <li>Personalizing user experience</li>
              <li>Conducting research and analysis</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">3.3 Communication</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Sending service-related notifications</li>
              <li>Providing customer support</li>
              <li>Sending marketing communications (with consent)</li>
              <li>Alerting you to policy changes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">4. Social Media Account Management</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">4.1 EXPRESS AUTHORIZATION & CONSENT</h3>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
              <p className="text-muted-foreground leading-relaxed">
                By using YASNO AI, you explicitly consent to:
              </p>
              <p className="text-muted-foreground italic leading-relaxed mt-2">
                "I grant YASNO Marketing LLC comprehensive authority to access, manage, and operate my social media accounts as my digital representative. This includes but is not limited to creating content, publishing posts, responding to engagement, and making strategic decisions autonomously based on AI analysis."
              </p>
            </div>

            <h3 className="text-lg font-medium mb-3 text-foreground">4.2 Account Setup for New Users</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">For users requesting new social media account setup:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>We collect necessary business information</li>
              <li>We create accounts on specified platforms</li>
              <li>We manage accounts during setup phase</li>
              <li>We transfer full control upon completion</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">4.3 Data Access Scope</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">We only access data necessary for service provision:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Posting and scheduling capabilities</li>
              <li>Analytics and reporting data</li>
              <li>Basic account information</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              We do not access private messages unless explicitly authorized.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">5. KILL SWITCH DATA HANDLING</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">5.1 Immediate Data Processing</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">When you activate the "STOP ALL AGENTS" (Kill Switch):</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>API tokens are immediately revoked</li>
              <li>All scheduled content is permanently deleted</li>
              <li>Active AI processes are terminated</li>
              <li>Social media access is severed</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">5.2 Data Retention Post-Termination</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">After Kill Switch activation or account termination:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Your data is retained for 30 days for recovery purposes</li>
              <li>After 30 days, all data is permanently deleted</li>
              <li>Backup systems may retain anonymized data for 90 days</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">6. Information Sharing and Disclosure</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">6.1 With Service Providers</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">We share information with:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>AI model providers (OpenAI, etc.)</li>
              <li>Cloud hosting services</li>
              <li>Payment processors</li>
              <li>Analytics platforms</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">6.2 Legal Requirements</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">We may disclose information if required by law or to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Comply with legal obligations</li>
              <li>Protect our rights and property</li>
              <li>Prevent fraud or security issues</li>
              <li>Protect user safety</li>
            </ul>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">6.3 Business Transfers</h3>
            <p className="text-muted-foreground leading-relaxed">
              In case of merger, acquisition, or sale, user information may be transferred.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">7. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">We implement:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Encryption of sensitive data</li>
              <li>Regular security assessments</li>
              <li>Access controls and monitoring</li>
              <li>Employee training on data protection</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">8. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">We retain your information:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>While your account is active</li>
              <li>For legal compliance requirements</li>
              <li>For service improvement purposes</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              You may request deletion of specific data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">9. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate information</li>
              <li>Delete your information</li>
              <li>Restrict or object to processing</li>
              <li>Data portability</li>
              <li>Withdraw consent</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">10. International Data Transfers</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your information may be transferred to and processed in countries with different data protection laws. We ensure appropriate safeguards are in place.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">11. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our Service is not intended for users under 16. We do not knowingly collect information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">12. Cookies and Tracking</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">We use cookies for:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Authentication and security</li>
              <li>Preferences and settings</li>
              <li>Analytics and improvement</li>
              <li>Advertising (with consent)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">13. AI Training Data</h2>
            
            <h3 className="text-lg font-medium mb-3 text-foreground">13.1 Content Usage for AI Training</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">You consent to our use of:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Generated content for AI model improvement</li>
              <li>Anonymized usage patterns</li>
              <li>Aggregated performance data</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              We do not use personally identifiable information for AI training without additional consent.
            </p>

            <h3 className="text-lg font-medium mb-3 mt-4 text-foreground">13.2 Opt-Out Options</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">You may opt out of specific data usage for AI training:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>In your account settings</li>
              <li>By contacting our support team</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">14. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy. We will notify you of significant changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-foreground">15. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">For privacy-related questions:</p>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 mt-2">
              <p className="text-muted-foreground">YASNO Marketing LLC</p>
              <p className="text-muted-foreground">Email: support@yasnomktg.com</p>
            </div>
          </section>
        </div>

        {/* Back to top */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <Button
            variant="outline"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="rounded-full"
          >
            Back to Top
          </Button>
        </div>
      </main>
    </div>
  );
}
